// src/rms/pages/JoinInvite.jsx
// ============================================================================
// /join — accepting an invitation
// ============================================================================
// Mounted OUTSIDE RequireRole, because the person arriving here may not have
// an account at all yet. That is the whole difficulty of this screen: it is
// the one place in the application reached by somebody who is not a member of
// anything, and it has to work before and after they sign in.
//
// ----------------------------------------------------------------------------
// The token comes out of the URL immediately
// ----------------------------------------------------------------------------
// A token in location.search is sent as the Referer header on every outbound
// subresource request the page makes. This application renders third-party ad
// units, so a token left in the address bar is a token handed to an ad network.
//
// So: read it, replaceState it away, keep it in sessionStorage. sessionStorage
// rather than a React ref because signing in navigates away and comes back,
// and the token has to survive that round trip without ever going back into
// the URL.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import memberService from "../data/memberService";
import { Banner } from "../components/ui";
import Brandmark from "../components/Brandmark";
import i18n from "../i18n";
import "../styles/rms.css";

/** Renders outside RmsProvider, so it translates through i18next directly. */
const t = (key, fallback, params) =>
  i18n.t(key, { defaultValue: fallback, ...(params || {}) });

const STASH = "rms.invite.token";

export default function JoinInvite() {
  const navigate = useNavigate();
  const { isAuthenticated, loading, authEnforced } = useAuth();

  const [state, setState] = useState("working");
  const [problem, setProblem] = useState(null);
  const attempted = useRef(false);

  // Strip the token before anything else can observe it. Runs before the
  // redemption effect because it has no dependencies and is declared first.
  const token = useRef(null);
  if (token.current === null) {
    const fromUrl = new URLSearchParams(window.location.search).get("token");
    if (fromUrl) {
      try {
        window.sessionStorage.setItem(STASH, fromUrl);
      } catch {
        /* private mode: the token stays in memory for this page load only */
      }
      window.history.replaceState({}, "", window.location.pathname);
      token.current = fromUrl;
    } else {
      try {
        token.current = window.sessionStorage.getItem(STASH) || "";
      } catch {
        token.current = "";
      }
    }
  }

  const redeem = useCallback(async () => {
    const result = await memberService.acceptInvite(token.current);
    if (!result.success) {
      setProblem(result.error);
      setState("failed");
      return;
    }
    try {
      window.sessionStorage.removeItem(STASH);
    } catch {
      /* nothing useful to do */
    }
    setState("done");
    // Replace rather than push: Back must not return to a spent token.
    navigate("/rms", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (attempted.current) return;

    if (!token.current) {
      setProblem(t("join.invalid", "This invitation is not valid or has expired."));
      setState("failed");
      attempted.current = true;
      return;
    }

    // Wait for the session to resolve before deciding anybody is signed out,
    // or a slow auth check sends a legitimately signed-in person to /signin.
    if (authEnforced && loading) return;

    if (authEnforced && !isAuthenticated) {
      setState("needsAccount");
      return;
    }

    attempted.current = true;
    redeem();
  }, [isAuthenticated, loading, authEnforced, redeem]);

  return (
    <div className="rms-root rms-landing rms-auth">
      {/* Centred throughout rather than only at the head: this screen is a
          status message, not a form, so there is no column of labels for the
          body text to line up with. */}
      <div className="rms-auth-inner" style={{ textAlign: "center" }}>
        {/* Not a link, unlike the other two shells. Redemption starts on mount,
            and a wordmark that navigates away mid-flight would abandon it. */}
        <div className="rms-auth-brand">
          <Brandmark size="1.35em" />
        </div>

        {state === "working" || state === "done" ? (
          <p style={{ color: "var(--rms-muted-strong)", fontSize: 14.5 }}>
            {t("join.working", "Adding you to the team…")}
          </p>
        ) : null}

        {state === "needsAccount" ? (
          <>
            <p style={{ fontSize: 14.5, marginBottom: 18 }}>
              {t("join.signInFirst", "Sign in — or create your account — and you will be added to the team straight away.")}
            </p>
            <button
              type="button"
              className="rms-btn rms-btn--primary"
              onClick={() => navigate("/signin", { state: { from: "/join" } })}
            >
              {t("join.continue", "Continue")}
            </button>
            <p className="rms-help" style={{ marginTop: 14 }}>
              {t("join.useInvitedAddress", "Use the address the invitation was sent to — the link is tied to it.")}
            </p>
          </>
        ) : null}

        {state === "failed" ? (
          <>
            <Banner tone="danger">{problem}</Banner>
            <p className="rms-help" style={{ marginTop: 14 }}>
              {t("join.askAgain", "Ask whoever invited you to send a new invitation.")}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
